import { Asset, InvoiceStatus, Offer, OfferStatus, ProjectStatus } from "src/app/domains";
import { EvaAddress } from "src/app/domains/address";
import { PaymentCondition } from "src/app/domains/payment-condition";
import { Phone } from "src/app/domains/phone";
import { isInvoiceOverdue } from "src/app/shared/helpers/invoice-overdue.helper";

import { Contact } from "../../contact/domains/contact";
import { Tag } from "../../eva-company/domains/eva-company-tag";

export enum CustomerStatus {
  ENABLED = "ENABLED",
  DISABLED = "DISABLED",
}

export enum RelationType {
  CUSTOMER = "CUSTOMER",
  PROSPECT = "PROSPECT",
}

export enum CustomerType {
  INDIVIDUAL = "INDIVIDUAL",
  COMPANY = "COMPANY",
}

/** Email prérempli à l'envoi d'une facture : l'email de facturation du client, sinon `fallback`. */
export function invoiceRecipient(
  customer: Pick<Customer, "mail_invoice"> | null | undefined,
  fallback: string | null | undefined,
): string | null {
  return customer?.mail_invoice?.trim() || fallback || null;
}

/** Contacts destinataires d'un envoi : le contact unique, sinon la sélection, sinon le principal d'un particulier. */
export function recipientContactIds(
  customer: Pick<Customer, "customer_type"> | null | undefined,
  contacts: Pick<Contact, "contact_id">[] | null | undefined,
  selectedIds: string[] | null | undefined,
): string[] {
  if (contacts?.length === 1) return [contacts[0].contact_id];
  if (selectedIds?.length) return [...selectedIds];
  if (customer?.customer_type === CustomerType.INDIVIDUAL && contacts?.length) return [contacts[0].contact_id];
  return [];
}

/** Vrai quand l'utilisateur doit pouvoir choisir parmi les contacts du client. */
export function canPickCustomerContacts(
  customer: Partial<Pick<Customer, "customer_type" | "contacts" | "contacts_ids">> | null | undefined,
): boolean {
  const contactCount = Math.max(customer?.contacts?.length ?? 0, customer?.contacts_ids?.length ?? 0);
  return customer?.customer_type === CustomerType.COMPANY || contactCount > 1;
}

/** Email principal d'un client entreprise (repli historique de l'envoi des factures). */
export function companyOfficeMail(
  customer: Pick<Customer, "mail_office" | "customer_type"> | null | undefined,
): string | null {
  return customer?.customer_type === CustomerType.COMPANY ? customer.mail_office || null : null;
}

export class Customer {
  customer_id: string = "";
  customer_corporate_id: string = "";
  eva_company_id: string = "";
  name: string = "";
  address: string = "";
  address_lat: number = 0;
  address_lng: number = 0;
  contact: string = "";
  internal_notes: string = "";
  contacts: Contact[] = [];
  addresses: EvaAddress[] = [];
  tags_ids: string[];
  tags: Tag[] = [];
  local_company_id: string = "";
  vat_number: string = "";
  default_income_category: string = "";
  /** Vide = conditions par défaut de l'entreprise. */
  payment_conditions: PaymentCondition[] = [];

  created_on?: Date;
  created_by?: string;
  created_by_name?: string;
  country: string;

  updated_on?: Date;
  updated_by?: string;
  updated_by_name?: string;

  status: string = CustomerStatus.ENABLED;
  relation: string = RelationType.CUSTOMER;

  contacts_ids: string[] = [];

  customer_address: EvaAddress = new EvaAddress();
  phone_office: string = "";
  phone_mobile: string = "";
  mobile_phone_details: Phone;
  office_phone_details: Phone;
  mail_office: string = "";
  mail_invoice: string = "";
  metadata = {};
  customer_type: CustomerType = CustomerType.COMPANY;
  assets: Asset[];

  offers: Offer[] = [];

  constructor() {
    this.country = "CH";
  }

  public static createInstance(jsonData: Customer): Customer {
    const c = Object.assign(new Customer(), jsonData);
    c.created_on = jsonData.created_on ? new Date(jsonData.created_on) : null;
    c.updated_on = jsonData.updated_on ? new Date(jsonData.updated_on) : null;
    if (jsonData.offers) {
      c.offers = Offer.createInstances(jsonData.offers);
    }

    if (jsonData.customer_address) {
      c.customer_address = EvaAddress.createInstance(jsonData.customer_address);
    }
    if (jsonData.addresses) {
      c.addresses = EvaAddress.createInstances(jsonData.addresses);
    } else {
      c.addresses = [];
    }
    if (jsonData.tags) {
      c.tags = Tag.createInstances(jsonData.tags);
    } else {
      c.tags = [];
    }

    if (jsonData.contacts) {
      c.contacts = Contact.createInstances(jsonData.contacts);
    } else c.contacts = [];
    if (jsonData.mobile_phone_details) {
      c.mobile_phone_details = Phone.createInstance(jsonData.mobile_phone_details);
    }
    if (jsonData.office_phone_details) {
      c.office_phone_details = Phone.createInstance(jsonData.office_phone_details);
    }
    c.payment_conditions = (jsonData.payment_conditions ?? []).map((pc) => new PaymentCondition(pc));

    return c;
  }

  public static createInstances(jsonData: any[]): Customer[] {
    return jsonData.map(Customer.createInstance);
  }

  clone(): Customer {
    let s = Object.assign(new Customer(), this);
    s.offers = Offer.createInstances(s.offers);
    return s;
  }

  static get statusLabel() {
    return {
      [CustomerStatus.ENABLED]: "Activé",
      [CustomerStatus.DISABLED]: "Désactivé",
    };
  }

  excelExport(financialData = false) {
    return {
      id: this.customer_corporate_id,
      name: this.name,
      contact: this.contact,
      address: this.address,
      status: this.status,
    };
  }

  // CUSTOMER_NAME

  get customer_name() {
    if (this.customer_type === CustomerType.COMPANY) {
      return this.name;
    } else {
      return this.contacts[0].display_name;
    }
  }

  // OFFERS
  get onGoingOffers(): Offer[] {
    if (!this.offers) return [];
    return this.offers.filter((x) => x.status === OfferStatus.ON_GOING);
  }
  get offersValueOnGoing(): number {
    return this.onGoingOffers.reduce((tot, item) => tot + (item.total_bt || 0), 0);
  }
  get validatedOffers(): Offer[] {
    if (!this.offers) return [];
    return this.offers.filter((x) => x.status === OfferStatus.VALIDATED);
  }
  get offersValueValidated(): number {
    return this.validatedOffers.reduce((tot, item) => tot + (item.total_bt || 0), 0);
  }

  // PROJECTS
  get allProjects() {
    let projects = this.offers
      .filter((x) => [OfferStatus.ON_GOING, OfferStatus.VALIDATED].includes(x.status))
      .flatMap((x) => x.project)
      .filter((x) => x);
    let deduplicate_projects = [];
    for (let p of projects) {
      if (!deduplicate_projects.find((x) => x.project_id === p.project_id)) {
        deduplicate_projects.push(p);
      }
    }
    return deduplicate_projects;
  }
  get onGoingProjects() {
    return this.allProjects.filter((x) => x.status === ProjectStatus.ON_GOING);
  }
  get onGoingProjectsCount() {
    return this.onGoingProjects.length;
  }
  get terminatedProjects() {
    return this.allProjects.filter((x) => x.status === ProjectStatus.TERMINATED);
  }
  get terminatedProjectsCount() {
    return this.terminatedProjects.length;
  }

  // INVOICES
  get allValidInvoices() {
    return this.offers.flatMap((x) => x.validInvoices);
  }
  get allInvoices() {
    return this.offers.flatMap((x) => x.allInvoices);
  }

  get waitingSentInvoices() {
    return this.allValidInvoices.filter((x) => x.status === InvoiceStatus.VALIDATED);
  }
  get waitingSentInvoicesValuePc() {
    return this.waitingSentInvoices.reduce((tot, item) => tot + item.cd_total_bt_pc, 0);
  }

  get waitingPaymentInvoices() {
    return this.allValidInvoices.filter((x) => x.status === InvoiceStatus.SENT);
  }
  get waitingPaymentInvoicesValuePc() {
    return this.waitingPaymentInvoices.reduce((tot, item) => tot + item.leftToPayIncludingDiscountsPc, 0);
  }

  get latePaymentInvoices() {
    return this.allValidInvoices.filter((x) => isInvoiceOverdue(x));
  }
  get latePaymentInvoicesValuesPc() {
    return this.latePaymentInvoices.reduce((tot, item) => tot + item.cd_total_bt_pc, 0);
  }

  get allBilledInvoices() {
    return this.allValidInvoices.filter((x) => [InvoiceStatus.SENT, InvoiceStatus.PAID].includes(x.status));
  }
  get sumBilledPc() {
    return this.allBilledInvoices.reduce((tot, item) => tot + item.cd_total_bt_pc, 0);
  }
  get percentBilled() {
    let sumBilled = this.sumBilledPc;
    return sumBilled > 0 ? (sumBilled / this.offersValueValidated) * 100 : 0;
  }

  get paidInvoices() {
    return this.allValidInvoices.filter((x) => x.status === InvoiceStatus.PAID);
  }
  get paidInvoicesValues() {
    return this.allValidInvoices.reduce((tot, item) => tot + item.cd_paid_gross_total_price_pc, 0);
  }

  get allAddresses() {
    let a = "";
    if (this.addresses) {
      for (let address of this.addresses) {
        a += address.inlineAddress + "\n";
      }
    }
    return a;
  }
}
